import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, ArrowRight, Flame, Check } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
import { useCountdown } from '../hooks/useCountdown';
interface NavbarProps {
  selectedKitIds: string[];
  selectedUpsellIds: string[];
  onRemoveUpsell: (id: string) => void;
  onRemoveKit: (id: string) => void;
  onCheckoutAttempt: () => void;
}
function BrandLogo({
  size = 'md'
}: {
  size?: 'sm' | 'md';
}) {
  const dimensions = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  const recetasSize = size === 'sm' ? 'text-xl' : 'text-2xl';
  const powerfitSize = size === 'sm' ? 'text-base' : 'text-lg';
  return <div className="flex items-center gap-2.5" data-id="element-444">
      <img src="https://cdn.magicpatterns.com/uploads/6LW7dHCdvCYcF3WjzFZuRq/ChatGPT_Image_20_may_2026,_11_40_53_p.m..png" alt="Recetas PowerFit logo" className={`${dimensions} rounded-full object-cover shadow-sm`} data-id="element-445" />
      <div className="flex flex-col leading-none" data-id="element-446">
        <span className={`${recetasSize} text-combo-green leading-none`} style={{
        fontFamily: 'Pacifico, cursive',
        lineHeight: 1
      }} data-id="element-447">
          Recetas
        </span>
        <div className={`${powerfitSize} font-extrabold tracking-tight leading-none mt-0.5`} data-id="element-448">
          <span className="text-combo-green" data-id="element-449">Power</span>
          <span className="text-lime-600" data-id="element-450">Fit</span>
        </div>
      </div>
    </div>;
}
export function Navbar({
  selectedKitIds,
  selectedUpsellIds,
  onRemoveUpsell,
  onRemoveKit,
  onCheckoutAttempt
}: NavbarProps) {
  const [cartOpen, setCartOpen] = useState(false);
  const cartRef = useRef<HTMLDivElement>(null);
  const selectedKits = KITS.filter(k => selectedKitIds.includes(k.id));
  const selectedUpsells = UPSELLS.filter(u => selectedUpsellIds.includes(u.id));
  const itemCount = selectedKits.length + selectedUpsells.length;
  const total = selectedKits.reduce((sum, k) => sum + k.price, 0) + selectedUpsells.reduce((sum, u) => sum + u.price, 0);
  const oldTotal = selectedKits.reduce((sum, k) => sum + k.oldPrice, 0) + selectedUpsells.reduce((sum, u) => sum + u.oldPrice, 0);
  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (cartRef.current && !cartRef.current.contains(e.target as Node)) {
        setCartOpen(false);
      }
    }
    if (cartOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cartOpen]);
  return <motion.nav initial={{
    y: -100
  }} animate={{
    y: 0
  }} className="fixed top-9 left-0 right-0 z-50 bg-cream/85 backdrop-blur-md border-b border-ink/5 shadow-sm" data-id="element-451">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-0 md:h-20 grid grid-cols-[1fr_auto] md:grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 gap-y-2 md:gap-4" data-id="element-452">
        <BrandLogo data-id="element-453" />
        {/* Live Price Display */}
        <div className="hidden md:flex flex-col items-end justify-center" data-id="element-454">
          {itemCount > 0 ? <>
              <div className="flex items-center gap-2" data-id="element-455">
                <span className="text-xs text-ink-light font-medium uppercase tracking-wider" data-id="element-456">
                  Tu total
                </span>
                <span className="text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded" data-id="element-457">
                  50% OFF
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-458">
                <motion.span key={total} initial={{
              scale: 1.2,
              color: '#C9A84C'
            }} animate={{
              scale: 1,
              color: '#1A1A1A'
            }} transition={{
              duration: 0.4
            }} className="font-extrabold text-xl text-ink tabular-nums" data-id="element-459">
                  ${total.toLocaleString('es-AR')}
                </motion.span>
                <span className="text-xs text-ink-light line-through decoration-red-500/50" data-id="element-460">
                  ${oldTotal.toLocaleString('es-AR')}
                </span>
              </div>
            </> : <>
              <span className="text-xs text-ink-light line-through decoration-red-500/50" data-id="element-461">
                $29.980
              </span>
              <span className="font-bold text-lg text-ink" data-id="element-462">
                desde $14.990 ARS
              </span>
            </>}
        </div>
        {/* Cart Button + Popover */}
        <div className="relative justify-self-end md:col-start-3" ref={cartRef} data-id="element-463">
          <button onClick={() => setCartOpen(o => !o)} aria-label={`Carrito con ${itemCount} ${itemCount === 1 ? 'producto' : 'productos'}`} className="relative w-12 h-12 rounded-full bg-white border border-ink/10 hover:border-combo-green hover:shadow-md transition-all flex items-center justify-center text-ink" data-id="element-464">
            <ShoppingCart className="w-5 h-5" data-id="element-465" />
            {itemCount > 0 && <motion.span key={itemCount} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 20
          }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-cream" data-id="element-466">
                {itemCount}
              </motion.span>}
          </button>
          <AnimatePresence data-id="element-467">
            {cartOpen && <motion.div initial={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} transition={{
            duration: 0.18
          }} className="absolute right-0 mt-3 w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-premium-hover border border-ink/10 overflow-hidden z-50" data-id="element-468">
                {/* Header */}
                <div className="bg-ink text-white px-5 py-4 flex items-center justify-between" data-id="element-469">
                  <div className="flex items-center gap-2" data-id="element-470">
                    <ShoppingCart className="w-4 h-4" data-id="element-471" />
                    <span className="font-bold text-sm" data-id="element-472">Tu carrito</span>
                    {itemCount > 0 && <span className="text-xs text-white/60" data-id="element-473">
                        ({itemCount}{' '}
                        {itemCount === 1 ? 'producto' : 'productos'})
                      </span>}
                  </div>
                  <button onClick={() => setCartOpen(false)} aria-label="Cerrar" className="text-white/70 hover:text-white transition-colors" data-id="element-474">
                    <X className="w-4 h-4" data-id="element-475" />
                  </button>
                </div>
                {/* Body */}
                <div className="max-h-[400px] overflow-y-auto" data-id="element-476">
                  {itemCount === 0 ? <div className="px-5 py-10 text-center" data-id="element-477">
                      <div className="w-14 h-14 rounded-full bg-cream-dark mx-auto mb-4 flex items-center justify-center" data-id="element-478">
                        <ShoppingCart className="w-6 h-6 text-ink-light" data-id="element-479" />
                      </div>
                      <p className="text-sm text-ink-light mb-4" data-id="element-480">
                        Tu carrito está vacío.
                        <br data-id="element-481" />
                        Elegí tu kit para empezar.
                      </p>
                      <a href="#kits" onClick={() => setCartOpen(false)} className="inline-flex items-center gap-2 bg-combo-green text-white text-xs font-bold px-4 py-2.5 rounded-full hover:bg-combo-green/90 transition-all" data-id="element-482">
                        ELEGÍ TU KIT <ArrowRight className="w-3 h-3" data-id="element-483" />
                      </a>
                    </div> : <div className="divide-y divide-ink/5" data-id="element-484">
                      <AnimatePresence initial={false} data-id="element-485">
                        {selectedKits.map(kit => <motion.div key={kit.id} layout initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="px-5 py-3 flex items-start gap-3" data-id="element-486">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${kit.color} flex items-center justify-center text-white text-xs font-bold`} data-id="element-487">
                              KIT
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-488">
                              <div className="text-xs font-bold text-combo-green uppercase tracking-wide mb-0.5" data-id="element-489">
                                Kit principal
                              </div>
                              <div className="text-sm font-bold text-ink truncate" data-id="element-490">
                                {kit.name}
                              </div>
                              <div className="flex items-center gap-2 mt-1" data-id="element-491">
                                <span className="text-sm font-bold text-ink" data-id="element-492">
                                  ${kit.price.toLocaleString('es-AR')}
                                </span>
                                <span className="text-xs text-ink-light line-through" data-id="element-493">
                                  ${kit.oldPrice.toLocaleString('es-AR')}
                                </span>
                              </div>
                            </div>
                            <button onClick={() => onRemoveKit(kit.id)} aria-label={`Quitar ${kit.name}`} className="text-ink-light hover:text-red-600 transition-colors flex-shrink-0" data-id="element-494">
                              <X className="w-4 h-4" data-id="element-495" />
                            </button>
                          </motion.div>)}
                        {selectedUpsells.map(upsell => <motion.div key={upsell.id} layout initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="px-5 py-3 flex items-start gap-3" data-id="element-496">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{
                      backgroundColor: upsell.color
                    }} data-id="element-497">
                              +
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-498">
                              <div className="text-xs font-bold text-ink-light uppercase tracking-wide mb-0.5" data-id="element-499">
                                Complemento
                              </div>
                              <div className="text-sm font-bold text-ink truncate" data-id="element-500">
                                {upsell.name}
                              </div>
                              <div className="flex items-center gap-2 mt-1" data-id="element-501">
                                <span className="text-sm font-bold text-ink" data-id="element-502">
                                  ${upsell.price.toLocaleString('es-AR')}
                                </span>
                                <span className="text-xs text-ink-light line-through" data-id="element-503">
                                  ${upsell.oldPrice.toLocaleString('es-AR')}
                                </span>
                              </div>
                            </div>
                            <button onClick={() => onRemoveUpsell(upsell.id)} aria-label={`Quitar ${upsell.name}`} className="text-ink-light hover:text-red-600 transition-colors flex-shrink-0" data-id="element-504">
                              <X className="w-4 h-4" data-id="element-505" />
                            </button>
                          </motion.div>)}
                      </AnimatePresence>
                    </div>}
                </div>
                {/* Footer */}
                {itemCount > 0 && <div className="bg-cream-dark px-5 py-4 border-t border-ink/5" data-id="element-506">
                    <div className="flex items-center justify-between mb-1" data-id="element-507">
                      <span className="text-xs text-ink-light line-through" data-id="element-508">
                        ${oldTotal.toLocaleString('es-AR')}
                      </span>
                      <span className="text-xs font-bold text-combo-green" data-id="element-509">
                        Ahorrás ${(oldTotal - total).toLocaleString('es-AR')}
                      </span>
                    </div>
                    <div className="flex items-end justify-between mb-4" data-id="element-510">
                      <span className="text-sm font-bold text-ink uppercase tracking-wide" data-id="element-511">
                        Total
                      </span>
                      <motion.span key={total} initial={{
                  scale: 1.15,
                  color: '#C9A84C'
                }} animate={{
                  scale: 1,
                  color: '#1A1A1A'
                }} transition={{
                  duration: 0.35
                }} className="text-2xl font-extrabold tabular-nums" data-id="element-512">
                        ${total.toLocaleString('es-AR')}
                      </motion.span>
                    </div>
                    <a href="#cart" onClick={() => setCartOpen(false)} className="w-full bg-combo-green text-white text-sm font-bold py-3 rounded-full hover:bg-combo-green/90 transition-all shadow-glow flex items-center justify-center gap-2" data-id="element-513">
                      VER CARRITO Y CONFIRMAR <ArrowRight className="w-4 h-4" data-id="element-514" />
                    </a>
                  </div>}
              </motion.div>}
          </AnimatePresence>
        </div>
        <div className="col-span-2 md:col-span-1 md:col-start-4 w-full md:w-auto" data-id="element-515">
          <SmartCTA itemCount={itemCount} total={total} oldTotal={oldTotal} onCheckoutAttempt={onCheckoutAttempt} data-id="element-516" />
        </div>
      </div>
    </motion.nav>;
}
interface SmartCTAProps {
  itemCount: number;
  total: number;
  oldTotal: number;
  onCheckoutAttempt: () => void;
}
function SmartCTA({
  itemCount,
  total,
  oldTotal,
  onCheckoutAttempt
}: SmartCTAProps) {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const hasItems = itemCount > 0;
  const savings = oldTotal - total;
  // With items in cart → routed through App's checkout gate (shows the
  // bundle popup the first time, then goes to Shopify on subsequent clicks).
  if (hasItems) {
    return <motion.button type="button" onClick={onCheckoutAttempt} whileHover={{
      scale: 1.03
    }} whileTap={{
      scale: 0.97
    }} animate={{
      boxShadow: ['0 0 0 0 rgba(27,67,50,0.5)', '0 0 0 12px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} className="relative group bg-gradient-to-r from-combo-green via-combo-green to-combo-green/90 text-white px-3 sm:px-5 py-2 rounded-full hover:opacity-95 transition-all flex items-center gap-2 sm:gap-3 min-h-[48px] whitespace-nowrap shadow-glow border border-white/10 w-full md:w-auto justify-center md:justify-start" data-id="element-517">
        <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/15 flex items-center justify-center" data-id="element-518">
          <Check className="w-4 h-4" strokeWidth={3} data-id="element-519" />
        </div>
        <div className="flex flex-col items-start leading-tight" data-id="element-520">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider opacity-90" data-id="element-521">
            Finalizar compra
          </span>
          <div className="flex items-baseline gap-1.5" data-id="element-522">
            <span className="text-sm sm:text-base font-extrabold tabular-nums" data-id="element-523">
              ${total.toLocaleString('es-AR')}
            </span>
            {savings > 0 && <span className="hidden sm:inline text-[10px] font-bold text-yellow-300 tabular-nums" data-id="element-524">
                ahorrás ${savings.toLocaleString('es-AR')}
              </span>}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-525" />
      </motion.button>;
  }
  // Empty cart → urgency CTA with live countdown
  return <motion.a href="#kits" whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} animate={{
    boxShadow: ['0 0 0 0 rgba(220,38,38,0.45)', '0 0 0 14px rgba(220,38,38,0)', '0 0 0 0 rgba(220,38,38,0)']
  }} transition={{
    duration: 1.8,
    repeat: Infinity
  }} className="relative group bg-gradient-to-r from-red-600 via-red-600 to-red-700 text-white px-3 sm:px-4 py-2 rounded-full hover:opacity-95 transition-all flex items-center gap-2 sm:gap-3 min-h-[48px] whitespace-nowrap shadow-lg border border-red-400/40 w-full md:w-auto justify-center md:justify-start" data-id="element-526">
      <motion.div animate={{
      rotate: [0, 12, -12, 0],
      scale: [1, 1.15, 1]
    }} transition={{
      duration: 1.4,
      repeat: Infinity
    }} className="flex-shrink-0" data-id="element-527">
        <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" data-id="element-528" />
      </motion.div>
      <div className="flex flex-col items-start leading-tight" data-id="element-529">
        <span className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wider" data-id="element-530">
          Agarrá el 50% OFF
        </span>
        <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold tabular-nums text-yellow-200" data-id="element-531">
          <span data-id="element-532">termina en</span>
          <span className="bg-black/30 px-1.5 py-0.5 rounded" data-id="element-533">
            {hours}:{minutes}:{seconds}
          </span>
        </div>
      </div>
      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-534" />
    </motion.a>;
}
export { BrandLogo };